package com.ives.modules.mip;

import haxe.Timer;

import js.html.DivElement;
import js.html.MouseEvent;
import js.html.TouchEvent;
import js.html.WheelEvent;

import com.ives.utils.Remoting;
import com.ives.modules.vrc.ReconstructionSettings;
import com.ives.components.cube3d.Cube3D;
import com.ives.components.viewer.DicomViewer;
import com.ives.components.workspace.DicomWorkspace;
import com.ives.utils.Log;
import com.ives.utils.math.AxisAngle;
import com.ives.utils.math.MathStats;
import com.ives.utils.math.Quaternion;
import com.ives.utils.math.Vector2D;
import com.ives.utils.math.Vector3D;

@:abstract
class RayCastingClient extends Module{
	private static inline var DEFAULT_FOV = 35; // less fov looks more flat in DEGREES, standard, physical camera fov is ~40, but most pro rendering software uses 35
	private static inline var DEFAULT_SCREEN_SCALE = 0.1; // scale of the largest dimension of the volume

	private var startX:Float;
	private var startY:Float;
	private var prevX:Float;
	private var prevY:Float;
	private var pastX:Float;
	private var pastY:Float;

	private var momentum:AxisAngle;
	private var momentumTimer:Timer;

	private var down:Bool = false;

	private var cube:Cube3D = new Cube3D();

	private var rotation:Quaternion = Quaternion.identity();
	private var startRotation:Quaternion;
	private var cameraPosition:Vector3D = new Vector3D(0, -1, 0);
	private var cameraDistance:Float = 1000;
	private var screen:Vector2D;
	private var fov:Float;
	private var pixelSpacing:Float;

	private var timer:Timer;
	private var currentSettingsIndex:UInt = 0;
	private var settingSteps:Array<ReconstructionSettings>;

	private var holder:DivElement;

	override public function init(dicomViewer:DicomViewer, ?dicomWorkspace:DicomWorkspace){
		holder.style.display = "inline-block";

		dicomWorkspace.on("mousedown", onMouseDown);
		dicomWorkspace.on("mousemove", onMouseMove);
		dicomWorkspace.on("mouseup", onMouseUp);

		dicomWorkspace.on("touchstart", onTouchStart);
		dicomWorkspace.on("touchmove", onTouchMove);
		dicomWorkspace.on("touchend", onMouseUp);

		dicomWorkspace.on("wheel", onMouseWheel);

		Log.debug('Ray caster client init!');

		dicomWorkspace.append(cube);

		var min = Vector3D.fromArray(dicomWorkspace.dicomContextData.min);
		var max = Vector3D.fromArray(dicomWorkspace.dicomContextData.max);
		var dimnesions = max.clone().subtract(min);

		var maxVisibleDimension = Math.max(dimnesions.x, dimnesions.z); // y is the camera axis
		var reasonableScreenSize = maxVisibleDimension * DEFAULT_SCREEN_SCALE; // we do not want a big screen, because it is impossible to zoom into detail with it

		this.screen = new Vector2D(reasonableScreenSize, reasonableScreenSize);

		fov = DEFAULT_FOV;

		var radianFov = Math.PI * (DEFAULT_FOV / 180.0); // radians
		var fovSide:Float = Math.max(screen.x, screen.y) / 2;
		var screenDistance:Float = fovSide / Math.tan(radianFov / 2);

		cameraDistance = (screenDistance * (maxVisibleDimension/2) / fovSide) - screenDistance; // we should add half of depth, but considering that the patient is centered at the origin this works pretty good

		Log.info('For dimensions ${dimnesions} and fov ${fov} calculated screen is ${screen} at distance of ${screenDistance} (camera distance at ${cameraDistance}) and pixel spacing of ${pixelSpacing}');

		Remoting.volumeHistogram(dicomWorkspace.dicomImageData.sourceName, dicomWorkspace.dicomImageData.id, onHistogram);

		start();
	}

	private function onHistogram(histogram:Histogram){
		dicomWorkspace.dicomImage.remoteHistogram = histogram;

		trace(histogram);
	}

	private function superInit(dicomViewer:DicomViewer, ?dicomWorkspace:DicomWorkspace){
		super.init(dicomViewer, dicomWorkspace);
	}

	private function start(){
		if(timer != null)
			timer.stop();

		currentSettingsIndex = 0;

		update();
	}

	private function onMouseDown(event:MouseEvent){
		if(!dicomViewer.reconstructionsButtonChecked() || hasModifier(event))
			return;

		onDown(event.pageX, event.pageY);
	}

	private function onTouchStart(event:TouchEvent){
		event.preventDefault();

		if(!dicomViewer.reconstructionsButtonChecked())
			return;

		if(event.touches.length == 1)
			onDown(event.touches.item(0).pageX, event.touches.item(0).pageY);
	}

	private function onDown(x:Float, y:Float){
		if(!down){
			if(momentumTimer != null){
				momentumTimer.stop();
				momentumTimer = null;
			}

			startX = prevX = pastX = x;
			startY = prevY = pastY = y;
			startRotation = rotation.clone();

			down = true;
		}
	}

	private function onMouseMove(event:MouseEvent){
		if(!dicomViewer.reconstructionsButtonChecked() || hasModifier(event))
			return;

		onMove(event.pageX, event.pageY);
	}

	private function onTouchMove(event:TouchEvent){
		if(!dicomViewer.reconstructionsButtonChecked())
			return;

		if(event.touches.length == 1)
			onMove(event.touches.item(0).pageX, event.touches.item(0).pageY);
	}

	private function onMove(x:Float, y:Float){
		if(down){
			var perpendicular:Vector3D = new Vector3D(startY - y, 0, startX - x); // this is the axis rotation
			var angle:Float = MathStats.toRadians(perpendicular.length() / 4); // this is the angle of rotation
			var partialRotation:Quaternion = Quaternion.fromAxisAngle(perpendicular.normalize(), angle); // create the rotation
			var accumulatedRotation:Quaternion = startRotation.clone().multiply(partialRotation); // create accumulated rotation

			var temp:Array<Float> = accumulatedRotation.toArray();
			temp.push(cameraDistance);
			temp.push(fov);
			temp.push(pixelSpacing);

			updateCamera(temp);

			pastX = prevX;
			pastY = prevY;

			prevX = x;
			prevY = y;
		}
	}

	private function onMouseUp(event:MouseEvent){
		if(!dicomViewer.reconstructionsButtonChecked() || hasModifier(event))
			return;

		down = false;

		var past:Vector3D = new Vector3D(pastX, pastY);
		var prev:Vector3D = new Vector3D(prevX, prevY);
		var diff:Vector3D = prev.subtract(past);

		if(diff.length() > 10){
			var perpendicular:Vector3D = new Vector3D(-diff.y, diff.x, 0); // this is the axis rotation
			var angle:Float = MathStats.toRadians(perpendicular.length() / 4); // this is the angle of rotation

			momentum = new AxisAngle(perpendicular, angle);

			momentumTimer = Timer.delay(updateMomentum, 20);
		}
	}

	private function updateMomentum(){
		var partialRotation:Quaternion = Quaternion.fromAxisAngle(momentum.axis.normalize(), momentum.angle); // create the rotation
		var accumulatedRotation:Quaternion = rotation.clone().multiply(partialRotation); // create accumulated rotation

		var temp:Array<Float> = accumulatedRotation.toArray();
		temp.push(cameraDistance);
		temp.push(fov);
		temp.push(pixelSpacing);

		updateCamera(temp);

		momentum.angle *= 0.9;

		if(momentum.angle < 0.001){
			momentumTimer.stop();
			momentumTimer = null;
		}
		else
			momentumTimer = Timer.delay(updateMomentum, 20);
	}

	private function onMouseWheel(event:WheelEvent){
		var tempCameraDistance = cameraDistance + event.deltaY;

		var temp:Array<Float> = rotation.toArray();
		temp.push(tempCameraDistance);
		temp.push(fov);
		temp.push(pixelSpacing);

		updateCamera(temp);
	}

	private function update(){
		Log.debug('Update ray caster');

		var settings:ReconstructionSettings = settingSteps[currentSettingsIndex];

		Log.debug('Update VRC with settings ${settings}');

		pixelSpacing = Math.max(screen.x, screen.y) / (Math.max(settings.screenWidth, settings.screenHeight) - 1); // -1 because we sample at the end

		setUrl(settings);

		cube.setAxisAngleRotation(rotation.toAxisAngle());

		var startTheta:Float = MathStats.toDegrees(Math.acos(cameraPosition.y / cameraPosition.length()));
		var startPhi:Float = MathStats.toDegrees(Math.atan2(cameraPosition.z, cameraPosition.x));

		var camera:Vector3D = rotation.rotate(cameraPosition.clone());
		var theta:Float = MathStats.toDegrees(Math.acos(camera.y / camera.length())) - startTheta;
		var phi:Float = MathStats.toDegrees(Math.atan2(camera.z, camera.x)) - startPhi;

		currentSettingsIndex++;

		if(currentSettingsIndex < settingSteps.length){
			if(timer != null)
				timer.stop();

			timer = Timer.delay(update, settings.timeout);
		}
	}

	private function handleEvent(event:ModuleEvent){
		pixelSpacing = event.points.pop();
		fov = event.points.pop();
		cameraDistance = event.points.pop();

		rotation.fromArray(event.points);

		start();
	}

	private function setUrl(settings:ReconstructionSettings){}
	private function updateCamera(data:Array<Float>){}
}
